import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { PageTemplate } from '../components/PageTemplate';
import { Card, Button, Badge } from '../components/UnifiedUIComponents';
import { FaRobot, FaPlus, FaTrash, FaStar, FaPlug, FaEdit } from 'react-icons/fa';
import { aiProvidersAPI } from '../services/api';
import { ActionsMenu } from '../components/ActionsMenu';

const PROVIDER_TYPE_LABELS = {
  openrouter: 'OpenRouter',
  gemini: 'Google Gemini',
  deepseek: 'DeepSeek',
  anthropic: 'Anthropic',
  openai_compatible: 'Self-hosted / Custom (OpenAI-compatible)',
};

const emptyForm = () => ({ provider_type: 'openrouter', label: '', base_url: '', model: '', api_key: '', is_default: false });

const AIProviders = () => {
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isSuperAdmin = (currentUser?.role || '').toLowerCase() === 'super admin';

  const [providers, setProviders] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = form closed; object = editing/creating
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, presetsRes] = await Promise.all([aiProvidersAPI.getAll(), aiProvidersAPI.getPresets()]);
      setProviders(listRes.data.data || []);
      setPresets(presetsRes.data.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load AI providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); }, [load, isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <PageTemplate title="AI Providers" icon={FaRobot}>
        <div style={{ padding: '20px' }}><p>You do not have permission to access this page. Please contact a Super Admin.</p></div>
      </PageTemplate>
    );
  }

  const openCreate = () => { setForm(emptyForm()); setEditingId(null); };
  const openEdit = (p) => {
    setForm({ provider_type: p.provider_type, label: p.label, base_url: p.base_url, model: p.model, api_key: '', is_default: p.is_default });
    setEditingId(p.id);
  };
  const closeForm = () => { setForm(null); setEditingId(null); };

  const onTypeChange = (provider_type) => {
    const preset = presets[provider_type] || {};
    setForm((f) => ({ ...f, provider_type, base_url: f.base_url || preset.base_url || '', model: f.model || preset.model || '' }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await aiProvidersAPI.update(editingId, form);
        toast.success('Provider updated');
      } else {
        await aiProvidersAPI.create(form);
        toast.success('Provider added');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this AI provider configuration? It can be reactivated later.')) return;
    try {
      await aiProvidersAPI.delete(id);
      toast.success('Provider deactivated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Deactivate failed');
    }
  };

  const activate = async (p) => {
    try {
      await aiProvidersAPI.update(p.id, { label: p.label, base_url: p.base_url, model: p.model, is_active: true });
      toast.success('Provider activated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Activate failed');
    }
  };

  const setDefault = async (id) => {
    try {
      await aiProvidersAPI.setDefault(id);
      toast.success('Default provider updated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to set default');
    }
  };

  const testConnection = async (id) => {
    setTestingId(id);
    try {
      const res = await aiProvidersAPI.test(id);
      if (res.data.success) toast.success(`Connection OK — model replied: "${res.data.data.sample}"`);
      else toast.error(res.data.message || 'Connection test failed');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  return (
    <PageTemplate
      title="AI Providers"
      subtitle="Configure the LLM provider(s) that power the AI Assistant, protocol/report drafting, and risk/data review across the app."
      icon={FaRobot}
      headerAction={<Button variant="primary" onClick={openCreate}><FaPlus style={{ marginRight: 6 }} />Add Provider</Button>}
      loading={loading}
      isEmpty={!loading && providers.length === 0 && !form}
      emptyStateMessage="No AI provider configured yet. Add OpenRouter, Gemini, DeepSeek, Anthropic, or your own self-hosted OpenAI-compatible endpoint."
    >
      {form && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit Provider' : 'Add Provider'}</h3>
          <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '0.3rem' }}>Provider Type</label>
              <select className="form-control" value={form.provider_type} onChange={(e) => onTypeChange(e.target.value)} style={{ width: '100%' }}>
                {Object.keys(PROVIDER_TYPE_LABELS).map((key) => (
                  <option key={key} value={key}>{PROVIDER_TYPE_LABELS[key]}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '0.3rem' }}>Label</label>
              <input className="form-control" style={{ width: '100%' }} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Production OpenRouter" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '0.3rem' }}>Base URL</label>
              <input className="form-control" style={{ width: '100%' }} value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://... or http://your-server:port/v1" required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '0.3rem' }}>Model</label>
              <input className="form-control" style={{ width: '100%' }} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="e.g. deepseek-chat, gemini-2.0-flash" required />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: '0.3rem' }}>
                API Key {editingId ? '(leave blank to keep existing key)' : ''}
              </label>
              <input type="password" className="form-control" style={{ width: '100%' }} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={form.provider_type === 'openai_compatible' ? 'Optional for most self-hosted servers' : 'sk-...'} />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="is_default" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
              <label htmlFor="is_default" style={{ fontSize: 'var(--fs-md)' }}>Set as default provider (used by all AI features unless overridden)</label>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button type="button" variant="outline" onClick={closeForm}>Cancel</Button>
              <Button type="submit" variant="primary" loading={saving}>{editingId ? 'Save Changes' : 'Add Provider'}</Button>
            </div>
          </form>
        </Card>
      )}

      {providers.length > 0 && (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Type</th>
                  <th>Model</th>
                  <th>API Key</th>
                  <th>Default</th>
                  <th>Status</th>
                  <th style={{ width: '170px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.id}>
                    <td>{p.label}</td>
                    <td>{PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type}</td>
                    <td>{p.model}</td>
                    <td>{p.api_key_masked || <span style={{ color: 'var(--text-secondary)' }}>none</span>}</td>
                    <td>{p.is_default ? <Badge variant="success">Default</Badge> : <Button variant="outline" size="sm" onClick={() => setDefault(p.id)}><FaStar style={{ marginRight: 4 }} />Set default</Button>}</td>
                    <td>{p.is_active === false ? <Badge variant="danger">Inactive</Badge> : <Badge variant="success">Active</Badge>}</td>
                    <td className="action-cell" style={{ width: '170px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <Button variant="outline" size="sm" onClick={() => testConnection(p.id)} loading={testingId === p.id}><FaPlug style={{ marginRight: 4 }} />Test</Button>
                        <button className="btn btn-sm btn-outline" onClick={() => openEdit(p)} title="Edit">
                          <FaEdit />
                        </button>
                        <ActionsMenu items={[
                          p.is_active === false
                            ? { label: 'Activate', icon: <FaStar />, onClick: () => activate(p) }
                            : { label: 'Deactivate', icon: <FaTrash />, onClick: () => deactivate(p.id), danger: true },
                        ]} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </PageTemplate>
  );
};

export default AIProviders;
